import React, { useState, useEffect } from 'react';
import './SearchTenders.css';
import apiService from './api';

function SearchTenders() {
  // Restore from sessionStorage if available
  const [keyword, setKeyword] = useState(() => {
    return sessionStorage.getItem('searchKeyword') || '';
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(() => {
    const saved = sessionStorage.getItem('searchResults');
    return saved ? JSON.parse(saved) : [];
  });
  const [uploadingTenders, setUploadingTenders] = useState(new Set());
  const [downloadedReferenceNos, setDownloadedReferenceNos] = useState(new Set());
  const [error, setError] = useState('');

  // CAPTCHA modal state
  const [captchaModal, setCaptchaModal] = useState({
    isOpen: false,
    tenderId: null,
    tenderTitle: '',
    referenceNo: '',
    captchaImage: null,
    captchaText: '',
    isSubmitting: false
  });

  // Success modal state
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    totalFiles: 0,
    folderUrl: ''
  });

  // Processing overlay state
  const [processingTender, setProcessingTender] = useState(null);

  // Load already downloaded tenders on mount
  useEffect(() => {
    loadDownloadedTenders();
  }, []);

  // Save search state to sessionStorage for persistence between page switches
  useEffect(() => {
    sessionStorage.setItem('searchKeyword', keyword);
  }, [keyword]);

  useEffect(() => {
    sessionStorage.setItem('searchResults', JSON.stringify(searchResults));
  }, [searchResults]);

  const loadDownloadedTenders = async () => {
    try {
      const response = await apiService.listFirebaseTenders();
      if (response.success && response.tenders) {
        // Debug: log what we're getting
        console.log('Downloaded tenders from API:', response.tenders.map(t => ({
          ref: t.tender_reference_number,
          status: t.status
        })));

        // Only mark as downloaded if status is 'documents_uploaded' (case-insensitive)
        // Match by reference_no since that's what search results have
        // Trim values to handle whitespace differences
        const refs = new Set(
          response.tenders
            .filter(t => t.status && t.status.toLowerCase() === 'documents_uploaded')
            .map(t => (t.tender_reference_number || '').trim())
            .filter(Boolean)
        );
        console.log('Reference numbers marked as downloaded:', [...refs]);
        setDownloadedReferenceNos(refs);
      }
    } catch (error) {
      console.log('Could not load downloaded tenders:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!keyword.trim()) {
      setError('Please enter a search keyword');
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    setError('');

    // Refresh downloaded tenders list before showing results
    await loadDownloadedTenders();

    try {
      const response = await apiService.searchTenders(keyword.trim());

      if (response.success) {
        setSearchResults(response.results);
        setError('');
      } else {
        setError(response.message || 'Search failed');
      }
    } catch (error) {
      setError('Search error: ' + error.message);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleStartDownload = async (tender) => {
    setUploadingTenders(prev => new Set(prev).add(tender.id));
    setProcessingTender(tender.title); // Show processing overlay

    try {
      // Pass the full tender object including detail_url for direct navigation
      const response = await apiService.startDownload(tender);

      if (response.success && response.status === 'CAPTCHA_REQUIRED') {
        setProcessingTender(null); // Hide processing overlay
        // Show CAPTCHA modal
        setCaptchaModal({
          isOpen: true,
          tenderId: response.tender_id,
          tenderTitle: tender.title,
          referenceNo: tender.reference_no,  // Store reference for marking downloaded
          captchaImage: response.captcha_image,
          captchaText: '',
          isSubmitting: false
        });
      } else if (response.success) {
        setProcessingTender(null);
        // No CAPTCHA needed, download complete
        setDownloadedReferenceNos(prev => new Set(prev).add(tender.reference_no));
        alert(`✅ Tender "${tender.title}" downloaded successfully!`);
      } else {
        setProcessingTender(null);
        alert(`❌ Download failed: ${response.message || response.error}`);
      }
    } catch (error) {
      setProcessingTender(null);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setUploadingTenders(prev => {
        const newSet = new Set(prev);
        newSet.delete(tender.id);
        return newSet;
      });
    }
  };

  const handleCaptchaSubmit = async () => {
    if (!captchaModal.captchaText.trim()) {
      alert('Please enter the CAPTCHA text');
      return;
    }

    setCaptchaModal(prev => ({ ...prev, isSubmitting: true, errorMessage: '' }));

    try {
      const response = await apiService.submitCaptcha(
        captchaModal.tenderId,
        captchaModal.captchaText
      );

      // Invalid CAPTCHA — show new image and let user retry
      if (response.status === 'CAPTCHA_RETRY') {
        setCaptchaModal(prev => ({
          ...prev,
          isSubmitting: false,
          captchaText: '',
          errorMessage: response.error || 'Invalid CAPTCHA. Please try again.',
          captchaImage: response.captcha_image_base64 || prev.captchaImage
        }));
        return;
      }

      if (response.success) {
        // Immediately mark as downloaded (local state update) - trim for consistency
        if (captchaModal.referenceNo) {
          setDownloadedReferenceNos(prev => new Set(prev).add(captchaModal.referenceNo.trim()));
        }

        // Close modal
        setCaptchaModal({ isOpen: false, tenderId: null, tenderTitle: '', referenceNo: '', captchaImage: null, captchaText: '', isSubmitting: false, errorMessage: '' });

        // Also refresh from API (in background)
        loadDownloadedTenders();

        // Show success modal
        setSuccessModal({
          isOpen: true,
          totalFiles: response.total_files,
          folderUrl: response.folder_url
        });
      } else {
        setCaptchaModal(prev => ({
          ...prev,
          isSubmitting: false,
          captchaText: '',
          errorMessage: response.error || 'CAPTCHA failed. Please try again.'
        }));
      }
    } catch (error) {
      setCaptchaModal(prev => ({
        ...prev,
        isSubmitting: false,
        errorMessage: `Error: ${error.message}`
      }));
    }
  };

  const closeCaptchaModal = () => {
    setCaptchaModal({ isOpen: false, tenderId: null, tenderTitle: '', referenceNo: '', captchaImage: null, captchaText: '', isSubmitting: false, errorMessage: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  const isAlreadyDownloaded = (tender) => {
    // Check by reference_no since tender_id from search doesn't match Sheets
    // Trim to handle trailing whitespace in search results
    const refNo = (tender.reference_no || '').trim();
    const isDownloaded = downloadedReferenceNos.has(refNo);
    console.log(`Checking tender: "${refNo}" - Downloaded: ${isDownloaded}`);
    return isDownloaded;
  };

  return (
    <div className="search-tenders">
      {/* Processing Overlay */}
      {processingTender && (
        <div className="processing-overlay">
          <div className="processing-modal">
            <div className="processing-spinner"></div>
            <h3>Processing Download...</h3>
            <p>{processingTender}</p>
            <p className="processing-hint">Navigating to tender page and extracting metadata</p>
          </div>
        </div>
      )}

      {/* CAPTCHA Modal */}
      {captchaModal.isOpen && (
        <div className="captcha-modal-overlay">
          <div className="captcha-modal">
            <div className="captcha-modal-header">
              <h3>🔐 CAPTCHA Required</h3>
              <button className="close-button" onClick={closeCaptchaModal}>×</button>
            </div>
            <div className="captcha-modal-body">
              <p>Enter the CAPTCHA to download documents for:</p>
              <p className="tender-title-modal"><strong>{captchaModal.tenderTitle}</strong></p>

              {captchaModal.errorMessage && (
                <p className="captcha-error">{captchaModal.errorMessage}</p>
              )}

              {captchaModal.captchaImage && (
                <div className="captcha-image-container">
                  <img
                    src={`data:image/png;base64,${captchaModal.captchaImage}`}
                    alt="CAPTCHA"
                    className="captcha-image"
                  />
                </div>
              )}

              <input
                type="text"
                value={captchaModal.captchaText}
                onChange={(e) => setCaptchaModal(prev => ({ ...prev, captchaText: e.target.value, errorMessage: '' }))}
                placeholder="Enter CAPTCHA text"
                className="captcha-input"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleCaptchaSubmit()}
              />
            </div>
            <div className="captcha-modal-footer">
              <button
                className="cancel-button"
                onClick={closeCaptchaModal}
                disabled={captchaModal.isSubmitting}
              >
                Cancel
              </button>
              <button
                className="submit-captcha-button"
                onClick={handleCaptchaSubmit}
                disabled={captchaModal.isSubmitting || !captchaModal.captchaText.trim()}
              >
                {captchaModal.isSubmitting ? 'Submitting...' : 'Submit CAPTCHA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal.isOpen && (
        <div className="captcha-modal-overlay">
          <div className="captcha-modal success-modal">
            <div className="captcha-modal-header">
              <h3>Upload Complete</h3>
            </div>
            <div className="captcha-modal-body" style={{ textAlign: 'center' }}>
              <div className="success-icon">&#10003;</div>
              <p><strong>{successModal.totalFiles} file{successModal.totalFiles !== 1 ? 's' : ''}</strong> uploaded to Google Drive</p>
              {successModal.folderUrl && (
                <a
                  href={successModal.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="folder-link"
                >
                  Open Drive Folder
                </a>
              )}
            </div>
            <div className="captcha-modal-footer">
              <button
                className="submit-captcha-button"
                onClick={() => setSuccessModal({ isOpen: false, totalFiles: 0, folderUrl: '' })}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="search-section">
        <h2>Search Government Tenders</h2>
        <p className="api-status">Connected to Python Backend API</p>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Enter keyword (e.g., drone, software, construction)"
              className="search-input"
              disabled={isSearching}
            />
            <button
              type="submit"
              className="search-button"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search Tenders'}
            </button>
          </div>
        </form>

        {isSearching && (
          <div className="loading-message">
            🔍 Searching government websites for "{keyword}"...
            <br />
            <small>Backend API is processing your request...</small>
          </div>
        )}

        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}
      </div>

      {searchResults.length > 0 && (
        <div className="results-section">
          <h3>Search Results ({searchResults.length} tenders found)</h3>
          <div className="tender-grid">
            {searchResults.map((tender) => (
              <div key={tender.id} className={`tender-card ${isAlreadyDownloaded(tender) ? 'already-downloaded' : ''}`}>
                <div className="tender-header">
                  <h4>{tender.title}</h4>
                  <div className="tender-badges">
                    <span className="tender-id">{tender.reference_no}</span>
                    {isAlreadyDownloaded(tender) && (
                      <span className="downloaded-badge">✅ Already Downloaded</span>
                    )}
                  </div>
                </div>

                <div className="tender-info">
                  <div className="info-row">
                    <span className="label">Organization:</span>
                    <span>{tender.organization}</span>
                  </div>
                  {tender.location && (
                    <div className="info-row">
                      <span className="label">Location:</span>
                      <span>{tender.location}</span>
                    </div>
                  )}
                </div>

                <div className="tender-dates">
                  <div className="date-item">
                    <span className="date-label">Published:</span>
                    <span>{formatDate(tender.published_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">Closing:</span>
                    <span>{formatDate(tender.closing_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">Opening:</span>
                    <span>{formatDate(tender.opening_date)}</span>
                  </div>
                </div>

                <div className="tender-actions">
                  {isAlreadyDownloaded(tender) ? (
                    <button className="upload-button downloaded" disabled>
                      ✅ Already in Google Drive
                    </button>
                  ) : (
                    <button
                      className="upload-button"
                      onClick={() => handleStartDownload(tender)}
                      disabled={uploadingTenders.has(tender.id)}
                    >
                      {uploadingTenders.has(tender.id) ?
                        '⬇️ Starting Download...' :
                        '📥 Download to Google Drive'
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isSearching && searchResults.length === 0 && keyword && !error && (
        <div className="no-results">
          <p>No tenders found for "{keyword}". Try different keywords.</p>
          <small>Backend search completed successfully.</small>
        </div>
      )}
    </div>
  );
}

export default SearchTenders;
